import { Backdrop, Box, Button, Fade, IconButton, Modal, Typography } from "@mui/material";

import makeStyles from '@mui/styles/makeStyles';

import { useCallback, useEffect, useRef, useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import { ReactComponent as CopyIcon } from '../../Icons/copy.svg';
import { requestGameCode } from "../../Utils/Api";
import CategoryBlock from "../Block/CategoryBlock";
import BasicButton from "../Buttons/BasicButton";
import Nl2br from "../Utils/Nl2br";

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
    },
    paper: {
        maxWidth: '830px',
        backgroundColor: '#121212',
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        margin: '30px',
        position: "relative",
    },
    modalHeader: {
        display: 'inline-block',
        '& .MuiTypography-caption': {
            fontSize: '10px',
            color: theme.palette.secondary.light,
            letterSpacing: '1.5px',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1)
        },
        '& .MuiTypography-body1': {
            fontWeight: theme.typography.fontWeightRegular,
            fontSize: '20px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
        }
    },
    btnClose: {
        float: 'right'
    },
    modalClose: {
        textAlign: 'right',
        float: 'right',
        color: theme.palette.secondary.muted,
        marginTop: theme.spacing(1.5),
        // marginBottom: theme.spacing(1.5),
        clear: 'both'
    },
    contentBox: {
        marginBottom: theme.spacing(2)
    },
    contentBlock: {
        display: "inline-block",
        float: "left",
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        width: `calc(50% - ${theme.spacing(2)})`,
        margin: theme.spacing(1),
        textAlign: "center",
        cursor: "pointer",
        '& .MuiTypography-subtitle1': {
            fontSize: '20px',
        },
        background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.11), rgba(255, 255, 255, 0.11)), #121212',
        '&:hover .MuiTypography-root': {
            color: theme.palette.primary.main
        }
    },
    bundleCategoryTitle: {
        clear: 'both',
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: '20px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        marginBottom: theme.spacing(2),
        paddingTop: theme.spacing(4)
    },
    caption: {
        color: theme.palette.primary.main
    },
    gameCodeDisplayWrap: {
        display: 'block',
        width: '100%',
        backgroundColor: '#2C2C2C',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: '#494848',
            '& svg path': {
                fill: theme.palette.primary.main
            },
        },
        '&:focus-within': {
            outline: '2px solid ' + theme.palette.primary.main,
        },
    },
    gameCodeDisplayWrapInner: {
        display: 'flex',
        alignItems: 'center',
    },
    gameCodeDisplayText: {
        flexGrow: 1,
        backgroundColor: 'transparent',
        color: 'inherit',
        border: 0,
        padding: '1.5rem .5rem',
        textAlign: 'center',
        fontSize: '1.25rem',
        wordBreak: 'break-all',
        resize: 'none',
        '&:focus': {
            border: 'none',
            outline: 'none',
            backgroundColor: 'hsla(0, 0%, 0%, .2)',
        },
    },
    gameCodeDisplayCopyButton: {
        backgroundColor: 'transparent',
        border: 'none',
        padding: '1rem .5rem',
        '&:focus': {
            border: 'none',
            outline: 'none',
            backgroundColor: 'hsla(0, 0%, 0%, .2)',
        },
    },
    link: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        '&:hover': {
            color: theme.palette.secondary.light,
            textDecoration: "underline",
        },
        '&:visited, &:active': {
            color: theme.palette.primary.main,
        }

    }
}));

export default function GamePlatformDialog({ platformData, hasEnabledCodes, awardId, entry, title, onPlatformSelected, open, handleClose }) {
    const classes = useStyles();

    const selectedPlatform = platformData?.find(platform => platform.code);
    const hasCodesAvailable = Boolean(selectedPlatform || platformData?.find(platform => platform.available));

    const [loadedGameKey, setLoadedGameKey] = useState(null);
    const [loadedGameKeyPlatformName, setLoadedGameKeyPlatformName] = useState(null);

    async function getPlatformCode(platformName) {
        console.log("Platform", platformName);
        const gameCodeResp = await requestGameCode(
            awardId,
            entry.id,
            platformName,
        );
        if (gameCodeResp.status === "OK" && gameCodeResp.code) {
            setLoadedGameKey(gameCodeResp);
            setLoadedGameKeyPlatformName(platformName)

            onPlatformSelected?.({
                gameCode: gameCodeResp.code,
                platformName,
            });
        }
    }

    const gameKeyCode = loadedGameKey?.code ?? selectedPlatform?.code;
    const gameKeyPlatformName = loadedGameKeyPlatformName ?? selectedPlatform?.name;

    const gameBundles = entry?.data?.gameBundles?.answer;

    return (
        <div>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                className={classes.modal}
                open={open}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                onClose={handleClose}
                disableAutoFocus
            >
                <Fade in={open}>
                    <div className={classes.paper}>
                        <div>
                            <div className={classes.modalHeader}>
                                <Typography
                                    variant="caption"
                                    id="transition-modal-title"
                                >
                                    {title.toUpperCase()}
                                </Typography>
                                {(hasEnabledCodes || entry.isFreeToPlay) &&
                                    <Typography
                                        component="h5"
                                        variant="body1"
                                        id="transition-modal-title"
                                    >
                                        {
                                            entry.isFreeToPlay
                                                ? "Free to play"
                                                : gameKeyPlatformName 
                                                    ? `${gameKeyPlatformName} redeem code` 
                                                    : 'Redeem Code'
                                        }
                                    </Typography>
                                }
                            </div>
                            <IconButton className={classes.btnClose} onClick={handleClose} size="large">
                                <CancelIcon />
                            </IconButton>
                        </div>

                        {gameKeyCode 
                            ? (
                                <GameCodeDisplay gameKey={gameKeyCode} />
                            )
                            : (entry.isFreeToPlay && entry.freeToPlayMessage.trim()
                                ? (
                                    <Typography
                                            component="h5"
                                            variant="body2"
                                            id="transition-modal-title"
                                            style={{ marginTop: '16px', marginBottom: '16px', marginRight: '10px' }}
                                        >
                                        <Nl2br text={entry.freeToPlayMessage} />
                                    </Typography>
                                )
                                : (hasCodesAvailable
                                    ? (
                                        <>
                                            <Typography
                                                component="h5"
                                                variant="body2"
                                                id="transition-modal-title"
                                                style={{ marginTop: '16px', marginBottom: '16px', marginRight: '10px' }}
                                            >
                                                {
                                                    entry.isFreeToPlay 
                                                        ? <Nl2br text={entry.freeToPlayMessage} />
                                                        : <>
                                                            Members can only redeem one code per game. All other platform codes will become
                                                            unavailable after the first code is redeemed. Please do not redeem a code if you
                                                            already have access to the game through a BAFTA bundle.
                                                        </>
                                                }
                                            </Typography>
                                            <Box display="row" alignItems="center" className={classes.contentBox}>
                                                {platformData
                                                    //?.filter(p => p.available && (!selectedPlatform || selectedPlatform === p.id))
                                                    ?.map((p) =>
                                                        <GamePlatformButton
                                                            key={p.name}
                                                            disabled={!p.available}
                                                            platformName={p.name}
                                                            handleOnClick={() => {
                                                                getPlatformCode(p.name);
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Box>
                                        </>
                                    )
                                    : ""
                                )
                            )
                        }
                        {(gameBundles?.length) &&
                            <>
                                <Typography
                                    component="h5"
                                    variant="h5"
                                    className={classes.bundleCategoryTitle}
                                >
                                    Bundles
                                </Typography>
                                <Typography
                                    component="p"
                                    variant="body2"
                                    style={{ marginTop: '16px', marginBottom: '16px', marginRight: '10px' }}
                                >
                                    BAFTA bundles are single-use codes which provide access to a number of entered
                                    games. Bundle codes can be found <a className={classes.link} href="https://view.bafta.org/award/4/category/491">here</a>.
                                </Typography>

                                {gameBundles
                                    ?.map(item => (
                                        <CategoryBlock
                                            style={{
                                                width: 'calc(50% - 16px)',
                                                display: "flex",
                                                justifyContent: "center",
                                                margin: '8px',
                                                float: "left",
                                                textAlign: "center"
                                            }}
                                            padding="12px 16px 8px"
                                            heading={item}
                                        />
                                    ))
                                }
                            </>
                        }
                        
                        <Button className={classes.modalClose} onClick={handleClose}>Close</Button>
                    </div>
                </Fade>
            </Modal>
        </div>
    );
}

function GamePlatformButton({ platformName, handleOnClick = null, disabled = null }) {
    const classes = useStyles();
    
    return (!disabled) ? (
        <>
            {platformName &&
                <BasicButton className={classes.contentBlock} onClick={!disabled ? handleOnClick : ''} tabIndex={0}
                    onKeyPress={!disabled ? handleOnClick : ''}>
                    <Typography component="h6" variant="subtitle1">
                        {platformName && platformName.toUpperCase()}
                    </Typography>
                </BasicButton>
            }
        </>
    ) : null;
}

function GameCodeDisplay({ gameKey }) {
    const classes = useStyles();
    const gameCodeElRef = useRef(null);

    const [wasCopied, setWasCopied] = useState(false);

    const copyToClipboard = useCallback(async () => {
        if (!gameCodeElRef?.current) {
            console.error("gameCodeElRef.current is not set so could not copy code");
            return;
        }
        const wasCopied = await copyElementToClipboard(gameCodeElRef.current);
        setWasCopied(wasCopied);
    }, []);

    /* 
        BV-899:
        Copy to clipboard webapi's (navigator.clipboard and execCommand()) aren't working when called using a react onClick event attribute.
        It now uses vanilla JS addEventHandler click event instead, which works in all tested browsers
    */
    useEffect(() => {
        document.getElementById("copyToClipboardButton")
            ?.addEventListener("click", copyToClipboard);
        return () => {
            document.getElementById("copyToClipboardButton")
                ?.removeEventListener("click", copyToClipboard);
        }
    }, [copyToClipboard])
    return (
        <>
            <p id="transition-modal-description">
                For more information about redeeming codes visit &nbsp;
                <a rel="noreferrer" className={classes.link} href='https://help.view.bafta.org/help-articles-category/redeeming-codes/' target="_blank">
                    BAFTA View Help
                </a>
            </p>
            <button 
                id="copyToClipboardButton"
                // BV-899: see comment above
                // onClick={copyToClipboard} 
                className={classes.gameCodeDisplayWrap}
            >
                <div className={classes.gameCodeDisplayWrapInner}>
                    <div
                        className={classes.gameCodeDisplayText}
                        ref={gameCodeElRef}
                    >
                        {gameKey}
                    </div>
                    <div className={classes.gameCodeDisplayCopyButton} onClick={copyToClipboard}>
                        <CopyIcon />
                    </div>
                </div>
            </button>
            {wasCopied && (
                <p>Copied!</p>
            )}
        </>
    )

}

async function copyElementToClipboard(el) {
    
    let copied = false;
    if(navigator?.clipboard?.writeText) {
        try {
            const text = el.innerText;
            await navigator.clipboard.writeText(text);
            copied = true;
        } catch (error) {
            console.error("copy error", error.message, error);
            copied = false;
        }
    }
    
    if(!copied) {
        if (window.getSelection) {
            if (window.getSelection().empty) { // Chrome
                window.getSelection().empty();
            } else if (window.getSelection().removeAllRanges) { // Firefox
                window.getSelection().removeAllRanges();
            }
        } else if (document.selection) { // IE?
            document.selection.empty();
        }

        if (document.selection) {
            const range = document.body.createTextRange();
            range.moveToElementText(el);
            range.select().createTextRange();
            copied = document.execCommand("copy");
        } else if (window.getSelection) {
            const range = document.createRange();
            range.selectNode(el);
            window.getSelection().addRange(range);
            copied = document.execCommand("copy");
        }
    }

    return copied;
}